import { Box, Skeleton, Typography } from "@mui/material";
import { useCallback } from "react";
import { GradientStar } from "../../styles/icons/gradient-star";
import { useDialog } from "../../utils/hooks/use-dialog";
import { Carousel } from "../discover/carousel/carousel";
import { SeverityPill } from "../generic/components/severity-pill";
import { usePageContentMx } from "../layout/components/page-content";
import { AdditionalServicesModal } from "./additional-services-modal";
import { useAdditionalServiceItems } from "./use-additional-service-items";

import type { DialogController } from "../../utils/hooks/use-dialog";
import type { CarouselItemProps } from "../discover/carousel/carousel";
import type { AdditionalServicesType } from "./type";
import type { AdditionalServiceItem } from "./use-additional-service-items";
import type { FC } from "react";

export const AdditionalServicesCarousel: FC<{ newPage: boolean }> = (newPage) => {
  const dialog = useDialog<{ service: AdditionalServicesType }>();
  const shuffledItems = useAdditionalServiceItems();
  const pageContentMx = usePageContentMx();

  if (shuffledItems?.length === 0) return null;

  return (
    <Box sx={{ backgroundColor: newPage ? null : "background.default" }}>
      <Box
        sx={{
          pb: 4,
          pt: 4,
          mx: pageContentMx,
        }}
      >
        <>
          <Box sx={{ pb: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, display: "flex", gap: 1, color: "text.primary" }}
            >
              <GradientStar />
              Do something special today
            </Typography>
          </Box>
          {shuffledItems && (
            <Carousel
              items={shuffledItems}
              gap={2}
              Item={ServiceCarouselItem}
              ItemExtraProps={{ dialog }}
              sx={(theme) => ({
                "--carousel-navigation-button-width": theme.spacing(6),
              })}
              itemWidth="24%"
              itemMinWidth="258px"
            />
          )}
          {!shuffledItems && <SkeletonCarousel />}
        </>
      </Box>
      <AdditionalServicesModal dialog={dialog} />
    </Box>
  );
};

const ServiceCarouselItem: FC<
  CarouselItemProps<
    AdditionalServiceItem,
    { dialog: DialogController<{ service: AdditionalServicesType }> }
  >
> = ({ value: { key, ...item }, extraProps: { dialog } }) => (
  <ServiceCard {...item} data-analytics-id={`homepage-services-carousel-${key}`} dialog={dialog} />
);

const ServiceCard: FC<{
  name: string;
  title: string;
  description: string;
  service: AdditionalServicesType;
  image: string;
  "data-analytics-id"?: string;
  dialog: DialogController<{
    service: AdditionalServicesType;
  }>;
}> = ({ name, title, description, service, image, "data-analytics-id": analyticsId, dialog }) => {
  const handleClick = useCallback(() => {
    dialog.handleOpen({ service });
  }, [dialog, service]);

  return (
    <Box
      data-analytics-id={analyticsId}
      onClick={handleClick}
      sx={{
        cursor: "pointer",
        minWidth: "258px",
        borderRadius: 2,
        overflow: "hidden",
        boxShadow: 2,
        backgroundColor: "white",
      }}
    >
      <Box
        sx={{
          position: "relative",
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: 2,
          backgroundColor: "white",
          display: "flex",
          flexDirection: "column",
          height: "160px",
        }}
      >
        <Box sx={{ position: "absolute", top: 12, left: 12, zIndex: 1 }}>
          <SeverityPill color="primary">{name}</SeverityPill>
        </Box>
        <Box
          component="img"
          src={image}
          alt={title}
          sx={{
            width: "100%",
            height: "100%",
            display: "block",
            objectFit: "cover",
          }}
        />
      </Box>
      <Box
        sx={{
          background: "linear-gradient(to right bottom, #FFDF00, #FFB300)",
          px: 2,
          py: 2,
          height: "120px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography variant="body1" color="black" sx={{ fontWeight: "bold", lineHeight: 1.4 }}>
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: "auto",
          }}
        >
          <Typography variant="body2" color="black" sx={{ fontSize: "0.79rem" }}>
            {description}
          </Typography>
          <Typography variant="body2" color="black" sx={{ fontWeight: 500 }}>
            ›
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const SkeletonCarousel: FC = () => (
  <Carousel
    items={Array.from({ length: 4 }).map((_, index) => ({ key: index.toString() }))}
    gap={2}
    Item={SkeletonCard}
    sx={(theme) => ({
      "--carousel-navigation-button-width": theme.spacing(6),
    })}
    itemWidth="24%"
    itemMinWidth="258px"
  />
);

const SkeletonCard: FC = () => (
  <Box
    sx={{
      minWidth: "258px",
      borderRadius: 2,
      overflow: "hidden",
      boxShadow: 2,
      backgroundColor: "white",
    }}
  >
    <Box sx={{ position: "relative", height: "160px" }}>
      <Skeleton variant="rectangular" width="100%" height="100%" sx={{ transform: "none" }} />
      <Skeleton
        variant="rounded"
        width={72}
        height={24}
        sx={{ position: "absolute", top: 12, left: 12, borderRadius: 999, transform: "none" }}
      />
    </Box>
    <Box
      sx={{
        px: 2,
        py: 2,
        height: "120px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Box>
        <Skeleton variant="text" width="85%" sx={{ transform: "none" }} />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Skeleton variant="text" width="55%" sx={{ transform: "none" }} />
      </Box>
    </Box>
  </Box>
);
